(function () {
  'use strict';

  var AUTHORITATIVE_INVALID = {
    BOOKING_NOT_FOUND: true,
    HOLD_EXPIRED: true,
    EXPIRED: true,
    CANCELLED: true,
    CONFLICT: true,
    DUPLICATE_HOLD: true,
    // The hold on this line was taken for a different variant, so it can never be
    // repaired into a valid line - only removed.
    VARIANT_MISMATCH: true
  };

  // A repair or removal has to reload the cart page to pick up Shopify's new totals,
  // which loses any message we were showing. Leave a one-shot note behind so the page
  // that comes back can explain what happened instead of a line silently vanishing.
  //
  // The note carries the removed product as well as the reason: removing the only
  // line empties the cart, and "we removed your item" with nothing to click is a
  // dead end. With the URL the customer gets a way back to rebook in one tap.
  var NOTICE_KEY = 'mode_booking_cart_notice';

  function takeNotice() {
    try {
      var value = sessionStorage.getItem(NOTICE_KEY);
      if (value) sessionStorage.removeItem(NOTICE_KEY);
      if (!value) return null;
      try {
        return JSON.parse(value);
      } catch (e) {
        // A note left by an older version of this script, which stored a bare string.
        return { reason: value };
      }
    } catch (e) {
      return null;
    }
  }

  function leaveNotice(reason, line) {
    try {
      sessionStorage.setItem(NOTICE_KEY, JSON.stringify({
        reason: reason,
        url: (line && line.url) || null,
        title: (line && line.title) || null
      }));
    } catch (e) {
      // Private mode, or storage is full. The reload still happens; only the
      // explanation is lost.
    }
  }

  function CartGuard(root) {
    this.root = root;
    this.proxyBase = root.dataset.proxyBase || '/apps/mode-booking';
    this.panel = root.querySelector('[data-cart-panel]');
    this.retry = root.querySelector('[data-cart-retry]');
    this.notice = root.querySelector('[data-cart-notice]');
    this.noticeText = root.querySelector('[data-cart-notice-text]');
    this.noticeLink = root.querySelector('[data-cart-notice-link]');
    this.timer = null;
    this.blocked = true;
    this.noticed = false;
    this.copy = {
      checkingTitle: root.dataset.messageCheckingTitle,
      checkingDetail: root.dataset.messageCheckingDetail,
      retry: root.dataset.messageRetry,
      networkTitle: root.dataset.messageNetworkTitle,
      networkDetail: root.dataset.messageNetworkDetail,
      attentionTitle: root.dataset.messageAttentionTitle,
      attentionDetail: root.dataset.messageAttentionDetail,
      requiredTitle: root.dataset.messageRequiredTitle,
      requiredDetail: root.dataset.messageRequiredDetail,
      heldTitle: root.dataset.messageHeldTitle,
      removedNotice: root.dataset.messageRemovedNotice,
      repairedNotice: root.dataset.messageRepairedNotice,
      rebook: root.dataset.messageRebook
    };
    this.bind();
    this.showNotice(takeNotice());
    // An empty cart has nothing to validate. Skip the round trip and let whatever
    // notice we just restored stand on its own - this is the path a customer lands
    // on when the guard has just removed their only line. The panel is hidden with
    // it: "Checking your appointment..." over an empty cart is nonsense, and its
    // placeholder text is rendered server-side so it does not clear itself.
    //
    // The count comes from the rendered lines, not from an attribute. An attribute
    // can be missing - an older template, an extension block that has not been
    // redeployed - and reading it as 0 would silently switch validation off, which
    // is the one failure mode this guard must never have.
    if (this.lines().length > 0) {
      this.validate();
    } else {
      this.setBlocked(false);
      if (this.panel) this.panel.hidden = true;
      this.root.hidden = !this.noticed;
    }
  }

  CartGuard.prototype.lines = function () {
    return Array.prototype.map.call(this.root.querySelectorAll('[data-cart-line]'), function (line) {
      return {
        key: line.dataset.key,
        variant_gid: 'gid://shopify/ProductVariant/' + line.dataset.variantId,
        quantity: parseInt(line.dataset.quantity, 10) || 0,
        hold_id: line.dataset.holdId || null,
        token: line.dataset.token || null
      };
    });
  };

  CartGuard.prototype.checkoutControls = function () {
    return document.querySelectorAll(
      '[name="checkout"], form[action*="/checkout"] [type="submit"], .additional-checkout-buttons button'
    );
  };

  CartGuard.prototype.setBlocked = function (blocked) {
    this.blocked = blocked;
    this.checkoutControls().forEach(function (control) {
      if (blocked) {
        if (!control.dataset.modeBookingDisabled) {
          control.dataset.modeBookingDisabled = control.disabled ? 'preserve' : 'added';
        }
        control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
      } else if (control.dataset.modeBookingDisabled) {
        if (control.dataset.modeBookingDisabled === 'added') control.disabled = false;
        delete control.dataset.modeBookingDisabled;
        if (!control.disabled) control.removeAttribute('aria-disabled');
      }
      control.classList.toggle('mode-booking-cart-checkout-blocked', blocked);
    });
  };

  CartGuard.prototype.message = function (title, detail, tone, retry) {
    this.root.hidden = false;
    this.panel.hidden = false;
    this.panel.dataset.tone = tone || 'info';
    this.panel.querySelector('strong').textContent = title;
    this.panel.querySelector('p').textContent = detail;
    this.retry.hidden = !retry;
  };

  CartGuard.prototype.showNotice = function (note) {
    if (!this.notice) return;
    var reason = (note && note.reason) || '';
    var parts = [];
    if (reason.indexOf('removed') !== -1 && this.copy.removedNotice) {
      parts.push(this.copy.removedNotice);
    }
    if (reason.indexOf('repaired') !== -1 && this.copy.repairedNotice) {
      parts.push(this.copy.repairedNotice);
    }
    this.noticed = parts.length > 0;
    if (this.noticeText) this.noticeText.textContent = parts.join(' ');
    else this.notice.textContent = parts.join(' ');

    if (this.noticeLink) {
      var canRebook = this.noticed && note && note.url;
      if (canRebook) {
        this.noticeLink.href = note.url;
        this.noticeLink.textContent = note.title
          ? (this.copy.rebook || 'Choose a new time for') + ' ' + note.title
          : (this.copy.rebook || 'Choose a new time');
      }
      this.noticeLink.hidden = !canRebook;
    }

    this.notice.hidden = !this.noticed;
    if (this.noticed) this.root.hidden = false;
  };

  CartGuard.prototype.validate = function () {
    var self = this;
    this.setBlocked(true);
    this.message(this.copy.checkingTitle, this.copy.checkingDetail);

    return fetch(this.proxyBase + '/cart/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ lines: this.lines() })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('validation failed');
        return response.json();
      })
      .then(function (body) {
        return self.apply(body.lines || []);
      })
      .catch(function () {
        self.setBlocked(true);
        self.message(
          self.copy.networkTitle,
          self.copy.networkDetail,
          'error',
          true
        );
      });
  };

  CartGuard.prototype.apply = function (results) {
    var self = this;
    var required = results.filter(function (line) { return line.required; });
    required.forEach(function (line) { self.lockQuantityControls(line.key); });
    var quantityRepairs = required.filter(function (line) {
      return line.code === 'BOOKING_QUANTITY_INVALID';
    });
    var removals = required.filter(function (line) {
      return AUTHORITATIVE_INVALID[line.code];
    });

    if (quantityRepairs.length || removals.length) {
      var notice = removals.length
        ? (quantityRepairs.length ? 'removed_repaired' : 'removed')
        : 'repaired';
      // Capture the product being removed while its DOM row still exists; after the
      // reload there is nothing left to look it up from.
      var removedLine = removals.length ? self.lineMetadata(removals[0].key) : null;
      var changes = quantityRepairs
        .map(function (line) { return self.changeLine(line.key, 1); })
        .concat(removals.map(function (line) { return self.changeLine(line.key, 0); }));
      return Promise.all(changes).then(function () {
        leaveNotice(notice, removedLine);
        window.location.reload();
      }).catch(function () {
        self.setBlocked(true);
        self.message(
          self.copy.attentionTitle,
          self.copy.attentionDetail,
          'error',
          true
        );
      });
    }

    var invalid = required.filter(function (line) { return !line.valid; });
    if (invalid.length) {
      this.setBlocked(true);
      this.message(
        this.copy.requiredTitle,
        this.copy.requiredDetail,
        'error',
        false
      );
      return Promise.resolve();
    }

    if (!required.length) {
      this.setBlocked(false);
      this.root.hidden = this.noticed ? false : true;
      if (this.noticed) this.panel.hidden = true;
      return Promise.resolve();
    }

    var first = required[0];
    this.setBlocked(false);
    this.startCountdown(first.expires_at, first.summary);
    return Promise.resolve();
  };

  /** The hidden metadata row this snippet rendered for one cart line. */
  CartGuard.prototype.lineElement = function (key) {
    return Array.prototype.find.call(
      this.root.querySelectorAll('[data-cart-line]'),
      function (line) { return line.dataset.key === key; }
    );
  };

  CartGuard.prototype.lineMetadata = function (key) {
    var element = this.lineElement(key);
    if (!element) return null;
    return { url: element.dataset.url || null, title: element.dataset.title || null };
  };

  CartGuard.prototype.lockQuantityControls = function (key) {
    var metadata = this.lineElement(key);
    if (!metadata) return;
    var cartItem = document.querySelector('[data-product-id="' + CSS.escape(key) + '"]');
    if (!cartItem && /^\d+$/.test(metadata.dataset.index || '')) {
      cartItem = document.getElementById('CartItem-' + metadata.dataset.index);
    }
    if (!cartItem) return;
    cartItem.querySelectorAll('[name="updates[]"], .qtybtn, quantity-input, .quantity').forEach(function (control) {
      // Locking the quantity must not trap the line in the cart. Avenue's remove
      // button shares the .qtybtn class with the +/- steppers, so a remove control
      // is skipped rather than hidden with the rest.
      if (control.matches && control.matches('[class*="remove"]')) return;
      if (control.matches && control.matches('[name="updates[]"], .qtybtn')) control.disabled = true;
      var container = control.closest('quantity-input, .quantity') || control;
      container.classList.add('mode-booking-cart-quantity-locked');
      container.setAttribute('aria-hidden', 'true');
    });
  };

  CartGuard.prototype.changeLine = function (key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (response) {
      if (!response.ok) throw new Error('cart update failed');
      return response.json();
    });
  };

  CartGuard.prototype.startCountdown = function (expiresAt, summary) {
    var self = this;
    clearInterval(this.timer);
    var appointment = summary && summary.appointment && summary.appointment.display;
    var workshop = summary && summary.location && summary.location.name;
    var tick = function () {
      var seconds = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      var minutes = Math.floor(seconds / 60);
      var remainder = String(seconds % 60).padStart(2, '0');
      self.message(
        self.copy.heldTitle + ' ' + minutes + ':' + remainder,
        [appointment, workshop].filter(Boolean).join(' at '),
        seconds < 300 ? 'error' : 'success',
        false
      );
      if (seconds <= 0) {
        clearInterval(self.timer);
        self.validate();
      }
    };
    tick();
    this.timer = setInterval(tick, 1000);
  };

  CartGuard.prototype.bind = function () {
    var self = this;
    this.retry.textContent = this.copy.retry;
    this.retry.addEventListener('click', function () { self.validate(); });
    document.addEventListener('submit', function (event) {
      var submitter = event.submitter;
      var checkout = submitter && (
        submitter.name === 'checkout' || submitter.closest('[name="checkout"]')
      );
      if (!self.blocked || !checkout) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      self.panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, true);
    new MutationObserver(function () { self.setBlocked(self.blocked); }).observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  function boot() {
    document.querySelectorAll('[data-mode-booking-cart]').forEach(function (root) {
      if (root.dataset.modeBookingReady) return;
      root.dataset.modeBookingReady = 'true';
      new CartGuard(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
